import React, { useState, useMemo } from "react";
import { TrashIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import { useDeleteResponse } from "../../../api/mutations";
import {
  useGetResponsesGrouped,
  useGetTopic,
  useGetUserPreferences,
} from "../../../api/queries";
import Card from "../../../componentLibrary/Card";
import Button from "../../../componentLibrary/Button";
import Spinner from "../../../componentLibrary/Spinner";
import AddReflectionModal from "../../../components/AddReflectionModal";
import { ResponseEnum, ResponseI, LessonI } from "../../../types";
import { useNotification } from "../../../hooks/useNotification";
import Tooltip from "../../../componentLibrary/Tooltip";
import Accordion from "../../../componentLibrary/Accordion";
import KeyTakeawaysList from "../../../componentLibrary/KeyTakeawaysList";
import ResponseModal from "../../../componentLibrary/ResponseModal";
import { ErrorDisplay } from "../../../utils/ErrorHandling";

export interface Journal extends ResponseI {
  createdAt: Date;
}

interface LessonTakeaway {
  lesson: LessonI;
  takeaways: {
    takeaway: string;
    responses: Journal[];
  }[];
}

export const JournalTab = ({ topicId }: { topicId: string }) => {
  const {
    data: groupedJournals,
    isError,
    isLoading,
    error: groupedJournalsError,
  } = useGetResponsesGrouped(topicId);
  const {
    data: topic,
    isLoading: topicLoading,
    isError: isTopicError,
    error: topicError,
  } = useGetTopic(topicId);
  const { data: userPreferences } = useGetUserPreferences();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const [isAddReflectionModalOpen, setIsAddReflectionModalOpen] =
    useState(false);
  const [journalToDelete, setJournalToDelete] = useState<string | null>(null);
  const [selectedTakeaway, setSelectedTakeaway] = useState<{
    takeaway: string;
    responses: Journal[];
    lessonTitle: string;
  } | null>(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  // Check if user is saving answers
  const isUserSavingAnswers = userPreferences?.saveAnswersToLog ?? true;

  // Create lesson takeaways structure and sort by topic lesson order
  const lessonTakeaways = useMemo(() => {
    if (!groupedJournals || !topic) return [];

    const lessonMap = new Map<string, LessonTakeaway>();

    groupedJournals.forEach((group) => {
      const lesson = group.lesson;
      if (!lesson) return;

      // Extract unique key takeaways from lesson pages
      const uniqueTakeaways = new Set(
        lesson.pages
          .map((page) => page.keyTakeaways)
          .filter((takeaway) => takeaway && takeaway.trim() !== ""),
      );

      if (!lessonMap.has(lesson._id)) {
        lessonMap.set(lesson._id, {
          lesson: lesson,
          takeaways: [],
        });
      }

      const lessonData = lessonMap.get(lesson._id)!;

      if (uniqueTakeaways.size === 0) {
        // If no key takeaways, create a single takeaway for the lesson title
        const lessonResponses = group.responses.filter(
          (response) =>
            (response.type === ResponseEnum.SECTION_RECAP ||
              response.type === ResponseEnum.ACTIVITY) &&
            response.title === lesson.title,
        ) as Journal[];

        lessonData.takeaways.push({
          takeaway: lesson.title,
          responses: lessonResponses,
        });
      } else {
        // Create takeaways for each unique takeaway
        uniqueTakeaways.forEach((takeaway) => {
          const takeawayResponses = group.responses.filter((response) => {
            if (
              response.type === ResponseEnum.SECTION_RECAP ||
              response.type === ResponseEnum.ACTIVITY
            ) {
              // Use keyTakeaway field if available, otherwise fall back to title matching
              return (
                response.keyTakeaway === takeaway ||
                (!response.keyTakeaway && response.title === takeaway)
              );
            }
            return false;
          }) as Journal[];

          lessonData.takeaways.push({
            takeaway,
            responses: takeawayResponses,
          });
        });
      }
    });

    // Sort lessons by their order in the topic
    const lessonTakeawaysArray = Array.from(lessonMap.values());
    if (topic.lessons && topic.lessons.length > 0) {
      lessonTakeawaysArray.sort((a, b) => {
        const aIndex = topic.lessons.findIndex((l) => l._id === a.lesson._id);
        const bIndex = topic.lessons.findIndex((l) => l._id === b.lesson._id);

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only one is found, put the found one first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // If neither is found, maintain original order
        return 0;
      });
    }

    return lessonTakeawaysArray;
  }, [groupedJournals, topic]);

  const handleTakeawayClick = (
    takeaway: string,
    responses: Journal[],
    lessonTitle: string,
  ) => {
    // Sort responses by createdAt (most recent first)
    const sortedResponses = responses.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });

    setSelectedTakeaway({
      takeaway,
      responses: sortedResponses,
      lessonTitle,
    });
    setCurrentCarouselIndex(0); // Reset to first item when opening
  };

  const handleCloseTakeawayModal = () => {
    setSelectedTakeaway(null);
    setCurrentCarouselIndex(0);
    setJournalToDelete(null); // Reset delete state when closing
  };

  const handleCarouselChange = (index: number) => {
    setCurrentCarouselIndex(index);
  };

  const deleteMutation = useDeleteResponse({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["responses"],
      });
      handleCloseTakeawayModal();
    },
  });

  const handleDelete = (id: string) => {
    setJournalToDelete(id);
  };

  const confirmDelete = () => {
    if (journalToDelete) {
      deleteMutation.mutate(journalToDelete);
      notify("success", "Entry deleted");
    }
  };

  const cancelDelete = () => {
    setJournalToDelete(null);
  };

  const getCurrentResponseId = () => {
    if (!selectedTakeaway || selectedTakeaway.responses.length === 0) return "";
    return selectedTakeaway.responses[currentCarouselIndex]?._id || "";
  };

  const getCurrentResponseTitle = () => {
    if (!selectedTakeaway || selectedTakeaway.responses.length === 0) return "";
    return selectedTakeaway.responses[currentCarouselIndex]?.title || "";
  };

  if (isLoading || topicLoading) return <Spinner className="mx-auto" />;
  if (isError || isTopicError || !groupedJournals || !topic)
    return (
      <ErrorDisplay
        error={groupedJournalsError || topicError}
        messageSource="api"
      />
    );

  return (
    <div className="flex flex-col h-full w-full">
      <div className="mx-auto w-full sm:min-w-80 sm:max-w-96 h-full flex flex-col">
        <div className="flex-1 px-2 py-4 overflow-y-auto">
          {lessonTakeaways.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-gray-500 text-center">
                Journal entries you create in the learn tab will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {lessonTakeaways.map((lessonGroup) => (
                <Card key={lessonGroup.lesson._id} className="mb-6">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                      {lessonGroup.lesson.icon?.src && (
                        <img
                          className="w-5 h-5"
                          src={lessonGroup.lesson.icon.src}
                          alt={
                            lessonGroup.lesson.icon.alt ||
                            lessonGroup.lesson.title
                          }
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      {lessonGroup.lesson.title}
                    </h3>
                    <Accordion
                      title={
                        <div className="flex items-center gap-2">
                          <div className="text-md font-bold">Key Takeaways</div>
                          <Tooltip
                            icon={<InformationCircleIcon className="w-4 h-4" />}
                            text="Your responses to key takeaways from this lesson."
                            position="right"
                            maxWidth={175}
                          />
                        </div>
                      }
                      defaultOpen={true}
                      headerClassName="p-2 w-full"
                    >
                      <KeyTakeawaysList
                        takeaways={lessonGroup.takeaways.map(
                          (takeaway, index) => {
                            return {
                              id: `${lessonGroup.lesson._id}-${index}`,
                              title: takeaway.takeaway,
                              // Show "View My Responses" whenever there are responses
                              // (previously required content with keys, which hid the button
                              // when content was empty or had unexpected structure)
                              hasResponses: takeaway.responses.length > 0,
                              responseCount: takeaway.responses.length,
                              onActionClick: () =>
                                handleTakeawayClick(
                                  takeaway.takeaway,
                                  takeaway.responses,
                                  lessonGroup.lesson.title,
                                ),
                            };
                          },
                        )}
                        actionLabel="View My Responses"
                        actionButtonClassName="max-w-32"
                      />
                    </Accordion>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Add the profile link at the bottom */}
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="text-center text-sm text-gray-600">
            If you would like to {isUserSavingAnswers ? "stop" : "start"} saving
            your answers, you can change your settings in{" "}
            <Link
              to="/profile"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              profile settings
            </Link>
          </div>
        </div>
      </div>

      <AddReflectionModal
        isModalOpen={isAddReflectionModalOpen}
        setIsModalOpen={setIsAddReflectionModalOpen}
      />
      <ResponseModal
        isOpen={!!selectedTakeaway}
        onClose={handleCloseTakeawayModal}
        responses={selectedTakeaway?.responses || []}
        currentResponseIndex={currentCarouselIndex}
        onResponseChange={handleCarouselChange}
        showTopicInfo={true}
        footer={
          journalToDelete ? (
            <div className="space-y-3">
              <div className="text-center text-sm text-gray-600">
                Are you sure you want to delete:{" "}
                <span className="font-semibold">
                  {getCurrentResponseTitle()}
                </span>
                ?
              </div>
              <div className="flex justify-center space-x-4">
                <Button size="sm" variant="alternative" onClick={cancelDelete}>
                  Cancel
                </Button>
                <Button size="sm" variant="cautionary" onClick={confirmDelete}>
                  Yes, Delete
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <Button.Circle
                variant="cautionary"
                className="p-1"
                onClick={() => handleDelete(getCurrentResponseId())}
              >
                <TrashIcon className="w-5 h-5" />
              </Button.Circle>
              <Button
                size="sm"
                variant="alternative"
                onClick={handleCloseTakeawayModal}
              >
                Close
              </Button>
            </div>
          )
        }
      />
    </div>
  );
};

export default JournalTab;
